import moment from 'moment';
import Api from 'services/PtmApi';
import { AppServices } from 'services/AppServices';
import { ApiError, returnApiError } from 'models/ApiError';
import { HttpStatusCode } from 'models/HttpStatusCode';
import { Region } from 'models/Region';
import { UserProvider } from 'components/contexts/UserContext';

export interface Session {
    accessToken?: string;
    refreshToken?: string;
    expiration?: number
    region?: Region;
}

interface AuthenticateResult {
    accessToken: string;
    refreshToken?: string;
    expiresIn: number;
    loginResult: HttpStatusCode;
    region: Region;
}

export class LoginService {
    private session: Session | undefined = undefined;
    private loginInProgress = false;

    private static readonly sessionStorageKey = 'session';

    constructor() {
        this.session = this.retrieveSession();
    }

    public getSession(): Readonly<Session> | undefined { return this.session; }

    private setSession(session: Session) {
        this.session = session;
    }

    private storeSession(session: Session) {
        window.localStorage.setItem(LoginService.sessionStorageKey, JSON.stringify(session));
    }

    public clearSession() {
        this.session = undefined;
        window.localStorage.removeItem(LoginService.sessionStorageKey);
    }

    private retrieveSession(): Session | undefined {
        const item = window.localStorage.getItem(LoginService.sessionStorageKey);
        return item === null ? undefined : JSON.parse(item) as Session;
    }

    private async restoreSession(): Promise<boolean> {
        const stored = this.retrieveSession();

        if (stored === undefined)
            return Promise.resolve(false);

        this.loginInProgress = true;
        this.setSession(stored);

        return this.verifySession()
            .then(result => {
                this.loginInProgress = false;
                return result;
            })
    }

    private onLogout() {
        AppServices.programService.programs = undefined;
        this.clearSession();
        UserProvider.clear();
    }

	/**
	 * Checks whether the error returned from the API is Unauthorized/InvalidAccessToken.
	 * If it is, LoginService invalidates its internal login state and returns a redirect
	 * path to LoginPage.
	 */
    public checkUnauthorizedErrorGetRedirectPath(errorReason: any) {
        const invalidToken = errorReason === ApiError.InvalidAccessToken;

        if (invalidToken)
            this.onLogout();

        return invalidToken ? '/login' : undefined
    }

    /**
	 * Try to reuse an existing access token.
	 * This verifies the session is still valid by getting the user information
	 * using the existing access token.
	 */
    public async verifySession(): Promise<boolean> {
        if (this.session === undefined) {
            console.debug("No session");
            return Promise.resolve(false);
        }

        if (typeof this.session.accessToken !== 'string') {
            console.debug(ApiError.InvalidAccessToken);
            return Promise.resolve(false);
        }

        let url = Api.getBaseUrl() + 'v2/token/verify';

        let init = {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + this.session.accessToken
            }
        };

        return fetch(url, init)
            .then(result => {
                if (result.status === HttpStatusCode.OK)
                    return true;
                else
                    // TODO: Utilize refresh token?
                    return false;
            })
            .catch(reason => {
                console.error(reason);
                return false;
            })
            .then(result => {
                if (result === true) {
                    console.debug('Session verified')
                }
                else {
                    console.debug('Session outdated')
                    this.clearSession();
                }
                return result;
            });
    }

	/**
	 * Log in a user using an email address and password.
	 * Returns a promise on a LoginResult that tells whether the log-in succeeded.
	 */
    public logIn(email: string, password: string): Promise<HttpStatusCode> {
        // User is not logged in and there's no callback in-progress
        if (this.loginInProgress)
            return Promise.reject(Error("Login in progress"));

        this.loginInProgress = true;
        this.session = undefined;

        return this.authenticateWithPassword(email, password)
            .then(async result => {
                if (result.loginResult === HttpStatusCode.OK) {
                    if (typeof result.accessToken !== 'string')
                        throw Error("No access token")

                    this.session = {
                        accessToken: result.accessToken,
                        refreshToken: result.refreshToken,
                        region: result.region,
                        expiration: moment.utc().add(result.expiresIn, 'seconds').unix()
                    };

                    console.debug(this.session);
                    this.storeSession(this.session);
                }

                this.loginInProgress = false;
                return result.loginResult;
            })
            .catch((reason) => {
                this.loginInProgress = false;
                console.error(reason);
                return Promise.reject(reason);
            });
    }

    /**
	 * Used for external authentications
	 * @param accessToken
	 * @param refreshToken
	 * @param expiresIn
	 */
    public async externalAuthentication(accessToken: string, refreshToken: string, expiresIn: number): Promise<boolean> {
        if (typeof accessToken !== 'string' || accessToken === null)
            return Promise.resolve(false);

        this.loginInProgress = true;

        this.session = {
            accessToken: accessToken,
            refreshToken: refreshToken,
            expiration: moment.utc().add(expiresIn, 'seconds').unix(),
            region: Region.Row
        };

        console.log(this.session);
        this.storeSession(this.session);

        return this.verifySession()
            .then(result => {
                this.loginInProgress = false;
                return result;
            });
    }

    private async authenticateWithPassword(email: string, password: string): Promise<AuthenticateResult> {
        let url = Api.getBaseUrl() + 'v2/token';

        let data = 'grant_type=password&username=' + encodeURIComponent(email) +
            '&password=' + encodeURIComponent(password);

        let init = {
            method: 'POST',
            body: data
        };

        const response = await fetch(url, init);
        if (response.status === HttpStatusCode.OK) {
            const responseContent = await response.json();

            if (typeof responseContent === 'object' &&
                typeof responseContent['access_token'] === 'string' &&
                typeof responseContent['expires_in'] === 'number') {
                const result: AuthenticateResult = {
                    accessToken: responseContent['access_token'],
                    refreshToken: responseContent['refresh_token'],
                    expiresIn: responseContent['expires_in'],
                    loginResult: response.status,
                    region: Api.getRegion()
                };

                return result;
            }
            else
                throw new Error("Response parsing failed");
        }
        else if (response.status === HttpStatusCode.Unauthorized) {
            const result: AuthenticateResult = {
                accessToken: "",
                expiresIn: 0,
                loginResult: response.status,
                region: Region.Row
            }

            return result;
        }
        else {
            const result: AuthenticateResult = {
                accessToken: "",
                expiresIn: 0,
                loginResult: response.status,
                region: Region.Row
            }

            return result;
        }
    }

    public logOut(): void {
        if (this.session) {
            if (typeof this.session.accessToken === 'string') {
                let url = Api.getBaseUrl() + 'v2/token';

                let init = {
                    method: 'DELETE',
                    headers: {
                        'Authorization': 'Bearer ' + this.session.accessToken
                    }
                };

                fetch(url, init)
                    .then(response => {
                        if (response.status === 200)
                            console.debug("Logged out");
                        else
                            console.debug("Failed to log out. Propably the account was deleted.")
                    })
                    .catch(console.error)
            }
        }

        this.onLogout();
    }

    public async requestPasswordResetMail(email: string): Promise<void> {
        if (this.session === undefined) {
            const emailAvailable = await this.checkEmailAvailability(email);

            if (emailAvailable === true)
                return Promise.resolve();

            const url = Api.getBaseUrl() + 'v2/password/request-reset';

            const content = {
                email: email
            };

            let init = {
                method: 'POST',
                headers: {
                    'X-Client-Id': Api.getClientId()
                },
                body: JSON.stringify(content)
            };

            return fetch(url, init)
                .then(response => {
                    if (response.status === HttpStatusCode.OK)
                        return;
                    else
                        return response.json().then(returnApiError) as Promise<void>;
                })
                .catch(Promise.reject);
        }

        return Promise.reject('Logged in');
    }

    public setPasswordWithResetToken(password: string, token: string): Promise<HttpStatusCode> {
        if (this.session === undefined) {
            const url = Api.getBaseUrl() + 'v2/password/reset/' + token;

            const content = {
                password: password
            };

            let init = {
                method: 'POST',
                headers: {
                    'X-Client-Id': Api.getClientId()
                },
                body: JSON.stringify(content)
            };

            return fetch(url, init)
                .then((response: Response) => {
                    return response.status;
                })
                .catch(reason => Promise.reject(reason));
        }

        return Promise.reject("Already has session");
    }

    public async resendVerification(email: string): Promise<void> {
        const url = Api.getBaseUrl() + 'v2/email/resend-verify';

        const content = { email: email };

        let init = {
            method: 'POST',
            headers: {
                'X-Client-Id': Api.getClientId()
            },
            body: JSON.stringify(content)
        };

        const response = await fetch(url, init);

        if (response.status === HttpStatusCode.OK) {
            return;
        }
    }

    public async checkEmailAvailability(email: string): Promise<boolean> {

        const url = Api.getBaseUrl() + 'v2/email/status';

        const content = {
            email: email
        };

        let init = {
            method: 'POST',
            headers: {
                'X-Client-Id': Api.getClientId()
            },
            body: JSON.stringify(content)
        };

        const response = await fetch(url, init);

        if (response.status === HttpStatusCode.OK) {
            var json = await response.text().catch(Promise.reject);

            if (typeof json === 'string') {
                const status = json.replace(/"/g, "");

                if (status === "reserved")
                    return false;
            }
        }

        return true;
    }
}